import React, { createContext, useState, useEffect, useCallback } from 'react';
import { BASE_URL, getPendingHitlRequests } from '../services/api';
import { getLeadDisplayName, isUnknownName } from '../utils/nameUtils';

export const HitlNotificationContext = createContext({
  notifications: [],
  unreadCount: 0,
  markAsRead: () => {},
  clearAll: () => {},
  toast: null,
});

const extractLeadEmail = (data) => {
  if (!data) return '';
  if (data.lead_email) return data.lead_email;
  if (data.email) return data.email;
  if (data.lead_id && String(data.lead_id).includes('@')) return data.lead_id;

  try {
    let personalInfoStr = data.lead_personal_info;
    if (!personalInfoStr && data.hitl_item) {
      personalInfoStr = data.hitl_item.lead_personal_info;
    }
    if (personalInfoStr) {
      const personalInfo = typeof personalInfoStr === 'string' ? JSON.parse(personalInfoStr) : personalInfoStr;
      if (personalInfo.lead_email) return personalInfo.lead_email;
      if (personalInfo.email) return personalInfo.email;
      if (personalInfo.lead_id && String(personalInfo.lead_id).includes('@')) return personalInfo.lead_id;
    }
  } catch (e) {}

  if (data.hitl_item) {
    if (data.hitl_item.lead_email) return data.hitl_item.lead_email;
    if (data.hitl_item.email) return data.hitl_item.email;
    if (data.hitl_item.lead_id && String(data.hitl_item.lead_id).includes('@')) return data.hitl_item.lead_id;
  }

  return '';
};

const extractLeadName = (data) => {
  let name = data.lead_name;
  
  // Try to parse from hitl_item or lead_personal_info if the backend changed the payload
  try {
    let personalInfoStr = data.lead_personal_info;
    if (!personalInfoStr && data.hitl_item) {
      personalInfoStr = data.hitl_item.lead_personal_info;
    }
    
    if (personalInfoStr) {
      const personalInfo = typeof personalInfoStr === 'string' ? JSON.parse(personalInfoStr) : personalInfoStr;
      if (personalInfo.first_name || personalInfo.last_name) {
        const parsedName = `${personalInfo.first_name || ''} ${personalInfo.last_name || ''}`.trim();
        if (parsedName && !isUnknownName(parsedName)) {
          name = parsedName;
        }
      }
    }
  } catch(e) {}
  
  const email = extractLeadEmail(data);
  return getLeadDisplayName(name, email, data.lead_id || 'Lead');
};

export const HitlNotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [socket, setSocket] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('access_token'));
  const [toast, setToast] = useState(null);
  const [isChatPage, setIsChatPage] = useState(window.location.pathname.startsWith('/chat'));

  // Automatically detect login/logout and route changes by polling
  useEffect(() => {
    const interval = setInterval(() => {
      const currentToken = localStorage.getItem('access_token');
      if (currentToken !== token) {
        setToken(currentToken);
      }
      const onChat = window.location.pathname.startsWith('/chat');
      if (onChat !== isChatPage) {
        setIsChatPage(onChat);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [token, isChatPage]);

  // Request Native Browser Notification permission using a user-gesture
  useEffect(() => {
    const handleFirstInteraction = () => {
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission().then(permission => {
          console.log("Notification permission:", permission);
        });
      }
    };
    
    // Modern browsers require a user interaction (like a click) before they show the prompt
    document.addEventListener('click', handleFirstInteraction, { once: true });
    
    return () => {
      document.removeEventListener('click', handleFirstInteraction);
    };
  }, []);

  useEffect(() => {
    const fetchInitialNotifications = async () => {
      if (!token || isChatPage) {
        setNotifications([]);
        return;
      }
      try {
        const pending = await getPendingHitlRequests();
        const pendingList = Array.isArray(pending)
          ? pending
          : Array.isArray(pending?.data)
          ? pending.data
          : Array.isArray(pending?.pending_requests)
          ? pending.pending_requests
          : Array.isArray(pending?.pending)
          ? pending.pending
          : Array.isArray(pending?.requests)
          ? pending.requests
          : Array.isArray(pending?.items)
          ? pending.items
          : [];
        const formattedPending = pendingList.map(data => ({
          id: data.hitl_id || data.id,
          type: 'hitl_pending',
          ...data,
          lead_email: extractLeadEmail(data),
          lead_name: extractLeadName(data)
        }));
        setNotifications(formattedPending);
      } catch (error) {
        console.error('Failed to fetch initial HITL notifications:', error);
      }
    };
    
    fetchInitialNotifications();
  }, [token, isChatPage]);

  useEffect(() => {
    let ws = null;
    let reconnectAttempts = 0;
    let reconnectTimeout = null;
    let pingInterval = null;
    let isMounted = true;

    const connect = () => {
      if (!token || !isMounted || isChatPage) return;

      const wsProtocol = BASE_URL.startsWith('https') ? 'wss' : 'ws';
      const host = BASE_URL.replace(/^https?:\/\//, '');
      const orgId = localStorage.getItem('org_id') || '';
      const queryStr = orgId ? `?org_id=${encodeURIComponent(orgId)}` : '';
      const wsUrl = `${wsProtocol}://${host}/hitl/notifications${queryStr}`;

      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log(`[HITL WebSocket] Connected successfully to ${wsUrl}`);
        reconnectAttempts = 0; // Reset attempts

        // Send periodic pings to keep connection alive through proxies
        pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send("ping");
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          if (event.data === "pong") return; // Ignore pong responses
          
          const data = JSON.parse(event.data);
          if (data && data.type === 'new_hitl') {
            console.log('[HITL WebSocket] Received new notification:', data);
            const leadEmail = extractLeadEmail(data);
            const newNotification = {
              hitl_id: data.hitl_id,
              lead_id: data.lead_id,
              trip_id: data.trip_id,
              category_code: data.category_code,
              priority: data.priority || 'MEDIUM',
              lead_email: leadEmail,
              lead_name: extractLeadName(data),
              created_at: data.created_at || new Date().toISOString(),
            };
            setNotifications((prev) => {
              // Prevent duplicates if the same hitl_id is received twice
              if (prev.some(n => n.hitl_id === newNotification.hitl_id)) {
                return prev;
              }
              return [newNotification, ...prev];
            });
            setToast({ ...newNotification, id: Date.now() });

            // Trigger Native Browser Notification
            if ("Notification" in window && Notification.permission === "granted") {
              const notification = new Notification(`Action required for ${newNotification.lead_name}`, {
                body: `Priority: ${newNotification.priority} | Type: ${newNotification.category_code}`,
                icon: '/favicon.ico',
                tag: newNotification.hitl_id
              });
              notification.onclick = () => {
                window.focus();
                notification.close();
                // Dispatch a custom event to navigate using the app's router
                const event = new CustomEvent('navigate-to-hitl', {
                  detail: {
                    leadId: newNotification.lead_id,
                    tripId: newNotification.trip_id,
                    hitlId: newNotification.hitl_id,
                    leadName: newNotification.lead_name,
                    leadEmail: newNotification.lead_email,
                    notification: newNotification
                  }
                });
                window.dispatchEvent(event);
              };
            }
          }
        } catch (err) {
          console.error(`[HITL WebSocket] Error parsing message: ${event.data}`, err);
        }
      };

      ws.onclose = (event) => {
        console.log(`[HITL WebSocket] Disconnected from ${wsUrl}. Code: ${event.code}`);
        if (pingInterval) clearInterval(pingInterval);
        
        if (isMounted && !isChatPage && event.code !== 1000) {
          reconnectAttempts += 1;
          const timeout = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
          console.log(`[HITL WebSocket] Reconnecting in ${timeout}ms... (Attempt ${reconnectAttempts})`);
          reconnectTimeout = setTimeout(() => {
            connect();
          }, timeout);
        } else {
          console.log('[HITL WebSocket] Component unmounted or on chat page, not reconnecting.');
        }
      };

      ws.onerror = (error) => {
        console.error(`[HITL WebSocket] Error occurred on connection to ${wsUrl}:`, error);
        // Do not call ws.close() here; onclose will be triggered natively if the connection drops.
      };

      setSocket(ws);
    };

    if (token && !isChatPage) {
      connect();
    }

    return () => {
      isMounted = false;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (pingInterval) clearInterval(pingInterval);
      if (ws) {
        if (ws.readyState === WebSocket.CONNECTING) {
          ws.onopen = () => {
            try { ws.close(1000, "Component unmounted"); } catch (e) {}
          };
          ws.onerror = () => {};
        } else if (ws.readyState === WebSocket.OPEN) {
          try { ws.close(1000, "Component unmounted"); } catch (e) {}
        }
      }
    };
  }, [token, isChatPage]);

  // Expose markAsRead to allow UI to dismiss notifications
  const markAsRead = (hitlId) => {
    setNotifications((prev) => prev.filter((n) => n.hitl_id !== hitlId));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  const unreadCount = notifications.length;

  return (
    <HitlNotificationContext.Provider value={{ notifications, unreadCount, markAsRead, clearAll, toast }}>
      {children}
    </HitlNotificationContext.Provider>
  );
};
